import axios from "axios";
import { API_URL } from "../../api/apiConfig";
import {
  downloadFeriaPhotoBlob,
  feriaSessionMediaUrl,
} from "../../api/Ferias/FeriasApi";

export const FERIA_PHOTO_DRAG_TYPE = "application/x-feria-photo";

const photoFileCache = new Map();

/** Arrastre nativo dock → proveedor (más fluido que hello-pangea con muchas fotos). */
let activeNativeDrag = null;

export function beginFeriaInboxPhotoDrag(event, photoIds = []) {
  const ids = (photoIds || [])
    .map(Number)
    .filter((id) => Number.isFinite(id) && id > 0);
  activeNativeDrag = { photoIds: ids };
  try {
    event.dataTransfer.setData(FERIA_PHOTO_DRAG_TYPE, JSON.stringify(ids));
    event.dataTransfer.setData("text/plain", ids.join(","));
  } catch {
    /* algunos browsers restringen setData */
  }
  event.dataTransfer.effectAllowed = "copy";
  const img = event.currentTarget?.querySelector?.("img");
  if (img && img.naturalWidth > 0) {
    try {
      event.dataTransfer.setDragImage(img, 36, 36);
    } catch {
      /* ignore */
    }
  }
}

export function clearFeriaInboxPhotoDrag() {
  activeNativeDrag = null;
}

export function feriaInboxDragPhotoIds() {
  return activeNativeDrag?.photoIds || [];
}

/** dragover a veces no enseña el tipo custom; usamos el flag en memoria. */
export function isFeriaPhotoDragEvent(event) {
  const types = [...(event?.dataTransfer?.types || [])];
  if (types.includes(FERIA_PHOTO_DRAG_TYPE)) return true;
  if (types.includes("Files") && feriaInboxDragPhotoIds().length) return true;
  return feriaInboxDragPhotoIds().length > 0;
}

export function readFeriaInboxPhotoDragIds(event) {
  try {
    const raw =
      event?.dataTransfer?.getData?.(FERIA_PHOTO_DRAG_TYPE) ||
      event?.dataTransfer?.getData?.("text/plain") ||
      "";
    if (raw) {
      if (raw.trim().startsWith("[")) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed
            .map(Number)
            .filter((id) => Number.isFinite(id) && id > 0);
        }
      }
      return String(raw)
        .split(/[,\s]+/)
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0);
    }
  } catch {
    /* fallback memoria */
  }
  return feriaInboxDragPhotoIds();
}

function asImageFile(blob, name) {
  if (!blob || blob.size < 32) return null;
  let type = String(blob.type || "").toLowerCase();
  if (
    type.includes("html") ||
    type.includes("json") ||
    type.includes("text/plain")
  ) {
    return null;
  }
  if (!type.startsWith("image/")) {
    if (/\.png$/i.test(name)) type = "image/png";
    else if (/\.webp$/i.test(name)) type = "image/webp";
    else if (/\.gif$/i.test(name)) type = "image/gif";
    else type = "image/jpeg";
  }
  return new File([blob], name, { type });
}

function photoFileName(photo, id) {
  return (
    String(photo?.originalName || photo?.fileName || "").trim() ||
    `feria-photo-${id || "x"}.jpg`
  );
}

function photoCandidateUrls(photo) {
  const urls = [];
  for (const raw of [photo?.fileUrl, photo?.thumbUrl, photo?.displayFileUrl]) {
    const url = feriaSessionMediaUrl(raw) || "";
    if (url && !urls.includes(url)) urls.push(url);
  }
  for (const raw of [photo?.fileUrl, photo?.thumbUrl]) {
    if (!raw || /^(https?:|blob:|data:)/i.test(String(raw))) continue;
    const base = String(API_URL || "").replace(/\/$/, "");
    const url = `${base}${String(raw).startsWith("/") ? "" : "/"}${raw}`;
    if (!urls.includes(url)) urls.push(url);
  }
  return urls;
}

async function blobFromUrlAxios(url) {
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";
  try {
    const { data } = await axios.get(url, {
      responseType: "blob",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      // uploads a veces van sin CORS completo; axios usa XHR
      withCredentials: false,
    });
    return data instanceof Blob ? data : null;
  } catch {
    return null;
  }
}

async function blobFromUrlFetch(url) {
  const token =
    typeof localStorage !== "undefined" ? localStorage.getItem("token") : "";
  const attempts = [];
  if (token) {
    attempts.push({
      mode: "cors",
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
  }
  attempts.push({ mode: "cors", cache: "no-store" });
  for (const init of attempts) {
    try {
      const res = await fetch(url, init);
      if (!res.ok) continue;
      const blob = await res.blob();
      if (blob.size < 32) continue;
      return blob;
    } catch {
      /* CORS / red */
    }
  }
  return null;
}

function fileFromImgElement(img, name) {
  if (!img || !img.naturalWidth) return null;
  try {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    if (!dataUrl.startsWith("data:image/")) return null;
    const bin = atob(dataUrl.split(",")[1] || "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    return asImageFile(new Blob([bytes], { type: "image/jpeg" }), name);
  } catch {
    // tainted canvas (CORS) → null
    return null;
  }
}

function fileFromDomThumb(photoId, name) {
  if (typeof document === "undefined") return null;
  const id = Number(photoId);
  if (!Number.isFinite(id) || id <= 0) return null;
  const root = document.querySelector(`[data-feria-photo-id="${id}"]`);
  const img = root?.querySelector?.("img");
  return fileFromImgElement(img, name);
}

async function fileFromImageUrl(url, name) {
  if (!url || typeof Image === "undefined") return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timer = setTimeout(() => resolve(null), 6000);
    img.onload = () => {
      clearTimeout(timer);
      resolve(fileFromImgElement(img, name));
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(null);
    };
    img.src = url;
  });
}

export function cachedFeriaPhotoFile(photo) {
  return photoFileCache.get(Number(photo?.id)) || null;
}

/** Descarga la foto de sesión como File (Vision / Materialoteca). */
export async function fetchFeriaPhotoAsFile(photo) {
  if (!photo) return null;
  const id = Number(photo.id);
  if (Number.isFinite(id) && id > 0) {
    const cached = photoFileCache.get(id);
    if (cached) return cached;
  }
  if (photo._localFile instanceof File) return photo._localFile;

  const name = photoFileName(photo, id);

  // 1) API autenticada (fiable; /uploads suele fallar por CORS/auth)
  if (Number.isFinite(id) && id > 0) {
    try {
      const blob = await downloadFeriaPhotoBlob(id);
      const file = asImageFile(blob, name);
      if (file) {
        photoFileCache.set(id, file);
        return file;
      }
    } catch {
      /* fallback abajo */
    }
  }

  // 2) Thumb ya visible en el tablero
  const fromDom = fileFromDomThumb(id, name);
  if (fromDom) {
    if (Number.isFinite(id) && id > 0) photoFileCache.set(id, fromDom);
    return fromDom;
  }

  const urls = photoCandidateUrls(photo);
  for (const url of urls) {
    // 2) axios (Bearer) — mejor que fetch en uploads del CRM
    let blob = await blobFromUrlAxios(url);
    if (!blob) blob = await blobFromUrlFetch(url);
    let file = asImageFile(blob, name);
    if (!file) file = await fileFromImageUrl(url, name);
    if (file) {
      if (Number.isFinite(id) && id > 0) photoFileCache.set(id, file);
      return file;
    }
  }
  return null;
}

/** Primer ancestro droppable de proveedor bajo el puntero (ignora el clone DnD). */
export function feriaSupplierDropFromPoint(clientX, clientY) {
  if (typeof document === "undefined") return null;
  const stack =
    typeof document.elementsFromPoint === "function"
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)].filter(Boolean);
  for (const el of stack) {
    if (!el || typeof el.closest !== "function") continue;
    // Ignora el fantasma / portal de @hello-pangea/dnd
    if (
      el.closest(
        "[data-rbd-drag-handle-dragging-id], [data-rbd-draggable-context-id][style*='fixed'], [data-rbd-overlay]",
      )
    ) {
      continue;
    }
    const node = el.closest("[data-feria-supplier-drop]");
    if (!node) continue;
    const supplierId = Number(node.getAttribute("data-feria-supplier-drop"));
    if (Number.isFinite(supplierId) && supplierId > 0) {
      return { supplierId, node };
    }
  }
  return null;
}
